import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Flame, RefreshCw, Minus, Plus, Lock, Unlock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "../App";

/**
 * FoundingSpotsAdminCard — founder/master-admin panel for the FOUNDING100
 * spots counter. Mounted on AdminPage above the main tab area so admins see
 * the live count without drilling into a sub-page.
 *
 * Capabilities:
 *  - Live progress visualisation (used / remaining / total)
 *  - "Release 1 spot" / "Claim 1 spot" quick buttons (uses_delta)
 *  - Manual adjustment offset (additive, e.g. reserve N for admin use)
 *  - Cap override (change the 100 ceiling if a campaign extends it)
 *
 * Backed by PATCH /api/founding-spots/admin (founder-only).
 */
const FoundingSpotsAdminCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [adjustDraft, setAdjustDraft] = useState(0);
  const [totalDraft, setTotalDraft] = useState(100);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/founding-spots/admin`);
      setStatus(r.data);
      setAdjustDraft(r.data.manual_adjustment ?? 0);
      setTotalDraft(r.data.total ?? 100);
    } catch (e) {
      // Non-founders get 403 — silently hide the card.
      if (e?.response?.status === 403) {
        setStatus("hidden");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  if (loading || status === "hidden" || !status) return null;

  const { total, used, remaining, closed, urgency_message, manual_adjustment } = status;
  const pct = total > 0 ? Math.min(100, ((total - remaining) / total) * 100) : 0;

  const patch = async (payload) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/founding-spots/admin`, payload);
      setStatus(r.data);
      toast.success("Founding spots updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="bg-white border border-[#E5DFEC] rounded-2xl p-5 sm:p-6 mt-4 mb-4"
      data-testid="founding-spots-admin-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${closed ? "bg-[#FEE2E2]" : "bg-[#FAF7F2]"}`}>
            {closed ? <Lock className="w-5 h-5 text-[#DC2626]" /> : <Flame className="w-5 h-5 text-[#8DB5A0]" />}
          </div>
          <div>
            <h3 className="text-base font-medium text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Founding Member Spots
            </h3>
            <p className="text-xs text-[#64748B]">
              FOUNDING100 — first {total} accounts lock in for life. {closed ? "Pool closed." : `${remaining} spots remaining.`}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={refresh}
          disabled={busy}
          className="rounded-full text-xs"
          data-testid="founding-spots-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Progress bar */}
      <div className="h-2 w-full rounded-full bg-[#F0ECF7] overflow-hidden mb-2">
        <div
          className="h-full bg-[#8DB5A0] transition-all duration-500"
          style={{ width: `${pct}%` }}
          data-testid="founding-spots-admin-bar"
        />
      </div>
      <p className="text-xs text-[#94A3B8] mb-4" data-testid="founding-spots-admin-stats">
        <strong className="text-[#6B4E8A]">{used}</strong> of {total} claimed · manual adjustment: <strong className="text-[#6B4E8A]">{manual_adjustment >= 0 ? "+" : ""}{manual_adjustment}</strong>
      </p>

      {urgency_message && (
        <p className="text-xs text-[#D97706] bg-[#FFFBEB] border border-[#FEF3C7] rounded-lg px-3 py-2 mb-4" data-testid="founding-spots-admin-urgency">
          {urgency_message}
        </p>
      )}

      {/* Manual controls */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl p-3">
          <p className="text-[11px] uppercase tracking-wider text-[#94A3B8] mb-2">Quick claim / release</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => patch({ used_delta: -1 })}
              disabled={busy || used <= 0}
              className="flex-1 rounded-full text-xs"
              data-testid="founding-spots-release"
            >
              <Minus className="w-3.5 h-3.5 mr-1" /> Release 1
            </Button>
            <Button
              size="sm"
              onClick={() => patch({ used_delta: 1 })}
              disabled={busy || remaining <= 0}
              className="flex-1 rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] text-xs"
              data-testid="founding-spots-claim"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Claim 1
            </Button>
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-1 leading-relaxed">
            Use Release if a member cancelled. Use Claim to reserve an admin spot.
          </p>
        </div>

        <div className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl p-3">
          <p className="text-[11px] uppercase tracking-wider text-[#94A3B8] mb-2">Manual adjustment</p>
          <div className="flex gap-2">
            <Input
              type="number"
              value={adjustDraft}
              onChange={(e) => setAdjustDraft(parseInt(e.target.value || "0", 10))}
              className="flex-1 h-8 text-xs"
              data-testid="founding-spots-adjust-input"
            />
            <Button
              size="sm"
              onClick={() => patch({ manual_adjustment: adjustDraft })}
              disabled={busy || adjustDraft === manual_adjustment}
              className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] text-xs"
              data-testid="founding-spots-adjust-save"
            >
              Save
            </Button>
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-1 leading-relaxed">
            Additive offset (+ reduces remaining, − adds back). Doesn't touch `used`.
          </p>
        </div>

        <div className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl p-3">
          <p className="text-[11px] uppercase tracking-wider text-[#94A3B8] mb-2">Pool cap</p>
          <div className="flex gap-2">
            <Input
              type="number"
              value={totalDraft}
              onChange={(e) => setTotalDraft(parseInt(e.target.value || "0", 10))}
              className="flex-1 h-8 text-xs"
              data-testid="founding-spots-total-input"
              min={1}
              max={10000}
            />
            <Button
              size="sm"
              onClick={() => patch({ total: totalDraft })}
              disabled={busy || totalDraft === total}
              className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] text-xs"
              data-testid="founding-spots-total-save"
            >
              Save
            </Button>
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-1 leading-relaxed">
            Bump from 100 → 150 to extend a campaign. Public counter updates instantly.
          </p>
        </div>
      </div>

      {closed && (
        <div className="mt-4 flex items-center gap-2 text-xs text-[#94A3B8]">
          <Unlock className="w-3.5 h-3.5" />
          To reopen the pool, increase the cap above or release spots via the quick controls.
        </div>
      )}
    </motion.div>
  );
};

export default FoundingSpotsAdminCard;
